import { useLocation } from 'react-router-dom'
import { Phone, X } from 'lucide-react'
import { ChatPanel } from './ChatPanel'
import { SHOW_SUPPORT_PHONE } from '../../lib/contact'
import { useChatWidget } from '../../hooks/useChatWidget'

/**
 * Global floating chat launcher (issue #37) - mounted once in App.tsx rather
 * than inside PageWrapper.tsx. PageWrapper looked like the natural home (it's
 * exactly where MobileCallBar already lives), but Home.tsx - the highest-
 * traffic page - builds its own layout and never uses PageWrapper, so mounting
 * there only would silently skip the homepage.
 *
 * Open/closed state lives in ChatWidgetProvider (useChatWidget.ts), not here
 * - issue #49's phone-number CTAs elsewhere on the page open the same panel
 * this launcher does, so the state can't be local to this component anymore.
 */
export function ChatWidget() {
  const location = useLocation()
  const { isOpen, toggle, close } = useChatWidget()

  // Staff/plumber-facing tools, not customer support.
  const hidden = /^\/(admin|portal)(\/|$)/.test(location.pathname)
  if (hidden) return null

  // MobileCallBar (only ever rendered when SHOW_SUPPORT_PHONE is true) pins a
  // full-width bar to the bottom of the viewport on mobile - lift the
  // launcher and panel above it there so they never overlap. Reads the same
  // flag MobileCallBar itself is gated on, so this stays correct automatically
  // if that flag is ever flipped back on.
  const fabBottom = SHOW_SUPPORT_PHONE ? 'bottom-20' : 'bottom-5'
  const panelBottom = SHOW_SUPPORT_PHONE ? 'bottom-36' : 'bottom-20'

  return (
    <>
      {isOpen && (
        <div className={`fixed ${panelBottom} sm:bottom-24 right-4 sm:right-6 z-50`}>
          <ChatPanel onClose={close} />
        </div>
      )}
      <button
        type="button"
        onClick={toggle}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
        className={`fixed ${fabBottom} sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-gold-500 hover:bg-gold-600 text-gray-900 shadow-lg shadow-gold-500/25 flex items-center justify-center transition-[transform,background-color,box-shadow] duration-200 hover:scale-105 hover:shadow-xl hover:shadow-gold-500/30 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2`}
      >
        {isOpen ? <X className="h-6 w-6" /> : <Phone className="h-6 w-6" />}
        {!isOpen && <span className="absolute inset-0 rounded-full ring-2 ring-gold-300 animate-ping opacity-75" />}
      </button>
    </>
  )
}
